import { getGlobalLocale } from 'i18n/globalLocale';

const LOCALE_MAP = {
  en: 'en-GB',
  bg: 'bg-BG',
  de: 'de-DE',
  fr: 'fr-FR',
  ro: 'ro-RO',
  tr: 'tr-TR',
  sr: 'sr-RS',
  mk: 'mk-MK',
  hu: 'hu-HU',
  sk: 'sk-SK',
  sl: 'sl-SI',
  es: 'es-ES',
  fi: 'fi-FI',
};

const resolveLocale = (overrideCode) => {
  const code = overrideCode || getGlobalLocale();
  return LOCALE_MAP[code] || code || 'en-GB';
};

const normalizeDate = (dateValue) => {
  if (!dateValue) return null;
  const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
};

const normalizeShortMonth = (locale, value) => {
  if (!value) return value;
  if (locale.toLowerCase().startsWith('bg')) {
    const trimmed = value.replace(/\.$/, '');
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }
  return value;
};

export const formatDateWithOptions = (
  dateValue,
  options,
  fallback = 'N/A',
  localeOverride
) => {
  if (!dateValue) {
    return fallback;
  }
  const date = normalizeDate(dateValue);
  if (!date) {
    return typeof dateValue === 'string' ? dateValue : fallback;
  }
  const locale = resolveLocale(localeOverride);
  const resolvedOptions = options || {};
  const formatter = new Intl.DateTimeFormat(locale, resolvedOptions);
  if (resolvedOptions.month === 'short') {
    const parts = formatter.formatToParts(date);
    return parts
      .map((part) =>
        part.type === 'month'
          ? normalizeShortMonth(locale, part.value)
          : part.value
      )
      .join('');
  }
  return formatter.format(date);
};

export const formatDate = (dateValue, localeOverride) =>
  formatDateWithOptions(
    dateValue,
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    },
    'N/A',
    localeOverride
  );

export const formatTime = (dateValue) => {
  if (!dateValue) {
    return 'N/A';
  }

  let date;
  if (typeof dateValue === 'string') {
    const trimmed = dateValue.trim();
    if (/^\d{1,2}:\d{2}$/.test(trimmed)) {
      const [hours, minutes] = trimmed.split(':');
      const paddedHours = hours.padStart(2, '0');
      date = new Date(`1970-01-01T${paddedHours}:${minutes}:00Z`);
    } else {
      date = new Date(trimmed);
    }
  } else {
    date = dateValue;
  }

  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return typeof dateValue === 'string' ? dateValue : 'N/A';
  }

  return date.toLocaleTimeString(resolveLocale(), {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
};

export function toMidnight(d) {
  return d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : undefined;
}
